/**
 * Azul tracker dashboard — grouped view from azul_tracker_review.html import.
 */

const STATUS_CHIP = {
  "IN PROGRESS": "bg-emerald-50 text-emerald-800 border-emerald-200",
  PENDING: "bg-amber-50 text-amber-800 border-amber-200",
  CLOSED: "bg-gray-100 text-gray-600 border-gray-200",
};

function statusChip(el, status) {
  const s = String(status || "—");
  const cls = STATUS_CHIP[s.toUpperCase()] || "bg-gray-50 text-gray-700 border-gray-200";
  return el("span", { class: `inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`, text: s });
}

function kpi(el, value, label) {
  return el("div", { class: "bg-white rounded-xl border border-gray-200 px-3 py-2 shadow-sm" }, [
    el("div", { class: "text-xl font-bold text-gray-900 tabular-nums", text: String(value) }),
    el("div", { class: "text-[10px] uppercase tracking-wide text-gray-500 mt-0.5", text: label }),
  ]);
}

export function renderAzulTrackerDashboard(ctx, project) {
  const { el } = ctx;
  const tracker = project?.azulTracker;
  if (!tracker?.sections?.length) return null;

  const m = tracker.metrics || {};
  const filterState = { q: "", status: "", priority: "" };

  const container = el("div", { class: "mb-6" });
  const metricsRow = el("div", { class: "grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-3" }, [
    kpi(el, m.subtopics ?? "—", "Subtopics"),
    kpi(el, m.topics ?? "—", "Main topics"),
    kpi(el, m.critical ?? "—", "Critical"),
    kpi(el, m.inProgress ?? "—", "In progress"),
    kpi(el, m.pending ?? "—", "Pending"),
    kpi(el, m.closed ?? "—", "Closed"),
    kpi(el, m.openActions ?? "—", "Open actions"),
  ]);

  const controls = el("div", { class: "flex flex-wrap gap-2 mb-3" }, [
    el("input", {
      type: "search",
      placeholder: "Search asset, note, action…",
      class: "flex-1 min-w-[160px] rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-800",
      oninput: (ev) => { filterState.q = String(ev.target.value || "").toLowerCase(); paint(); },
    }),
    el("select", {
      class: "rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-800",
      onchange: (ev) => { filterState.status = ev.target.value; paint(); },
    }, [
      el("option", { value: "", text: "All status" }),
      el("option", { value: "IN PROGRESS", text: "In progress" }),
      el("option", { value: "PENDING", text: "Pending" }),
      el("option", { value: "CLOSED", text: "Closed" }),
    ]),
    el("select", {
      class: "rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-800",
      onchange: (ev) => { filterState.priority = ev.target.value; paint(); },
    }, [
      el("option", { value: "", text: "All priority" }),
      el("option", { value: "CRITICAL", text: "Critical" }),
    ]),
  ]);

  const root = el("div", { class: "space-y-3" });

  function matches(sub) {
    if (filterState.status && sub.s !== filterState.status) return false;
    if (filterState.priority && sub.p !== filterState.priority) return false;
    if (!filterState.q) return true;
    const blob = [
      sub.i, sub.a, sub.o,
      ...(sub.n || []),
      ...(sub.x || []),
      ...(sub.t || []).map((e) => e[2]),
    ].join(" ").toLowerCase();
    return blob.includes(filterState.q);
  }

  function paint() {
    root.replaceChildren();
    for (const group of tracker.sections) {
      const subs = (group.subs || []).filter(matches);
      if (!subs.length) continue;
      const crit = subs.filter((s) => s.p === "CRITICAL").length;
      const groupEl = el("div", { class: "bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" });
      const head = el("button", {
        type: "button",
        class: "w-full flex items-center gap-3 px-3 py-2.5 text-left bg-gray-50 hover:bg-gray-100 border-b border-gray-100",
        onclick: () => body.classList.toggle("hidden"),
      }, [
        el("span", { class: "text-xs font-mono text-gray-400 w-5", text: group.sec }),
        el("span", { class: "text-sm font-semibold text-gray-900 flex-1", text: group.top }),
        el("span", { class: "text-xs text-gray-500", text: `${subs.length} subtopic${subs.length > 1 ? "s" : ""}${crit ? ` · ${crit} critical` : ""}` }),
      ]);
      const body = el("div", { class: "divide-y divide-gray-100" });
      for (const sub of subs) {
        const row = el("details", { class: "group" }, [
          el("summary", { class: "flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 list-none text-sm" }, [
            el("span", { class: "font-mono text-xs text-gray-400 w-9 shrink-0", text: sub.i }),
            el("span", { class: "font-medium text-gray-900 flex-1 min-w-0 truncate", text: sub.a || "—" }),
            sub.p === "CRITICAL" ? el("span", { class: "text-[10px] font-bold text-red-700 bg-red-50 px-1.5 py-0.5 rounded", text: "CRIT" }) : null,
            statusChip(el, sub.s),
            el("span", { class: "text-[11px] text-gray-400 tabular-nums shrink-0", text: sub.u && sub.u !== "—" ? sub.u : "" }),
          ]),
          el("div", { class: "px-3 pb-3 pt-1 bg-gray-50/80 text-xs space-y-2 border-t border-gray-100" }, [
            sub.o ? el("p", { class: "text-gray-500", text: `Owner: ${sub.o}` }) : null,
            (sub.n || []).length
              ? el("div", {}, [
                  el("div", { class: "text-[10px] font-bold uppercase text-amber-700 mb-1", text: "Open next actions" }),
                  ...(sub.n || []).map((a) => el("p", { class: "text-gray-800 pl-2 border-l-2 border-amber-300", text: a })),
                ])
              : null,
            (sub.x || []).length
              ? el("div", {}, [
                  el("div", { class: "text-[10px] font-bold uppercase text-indigo-700 mb-1", text: "From Excel minutes" }),
                  el("ul", { class: "space-y-1" }, (sub.x || []).slice(0, 8).map((x) =>
                    el("li", { class: "text-gray-700 bg-indigo-50/60 rounded px-2 py-1 leading-snug", text: x })
                  )),
                ])
              : null,
            (sub.t || []).length
              ? el("div", {}, [
                  el("div", { class: "text-[10px] font-bold uppercase text-emerald-700 mb-1", text: `Meeting timeline · ${sub.t.length} updates` }),
                  el("ul", { class: "space-y-1" }, [...(sub.t || [])].reverse().slice(0, 8).map(([date, src, text]) =>
                    el("li", { class: "flex gap-2 text-gray-700" }, [
                      el("span", { class: "font-mono text-[10px] text-blue-700 shrink-0 w-[4.5rem]", text: date }),
                      el("span", { class: "flex-1 leading-snug", text: text }),
                    ])
                  )),
                ])
              : null,
          ]),
        ]);
        body.appendChild(row);
      }
      groupEl.appendChild(head);
      groupEl.appendChild(body);
      root.appendChild(groupEl);
    }
    if (!root.childNodes.length) {
      root.appendChild(el("p", { class: "text-sm text-gray-500 py-4 text-center", text: "No items match your filters." }));
    }
  }

  container.appendChild(el("div", { class: "flex items-center justify-between gap-2 mb-2" }, [
    el("h3", { class: "text-sm font-bold text-gray-900 uppercase tracking-wide", text: "Azul progress tracker" }),
    el("span", { class: "text-[11px] text-gray-400", text: "Grouped by main topic · click subtopic for detail" }),
  ]));
  container.appendChild(metricsRow);
  container.appendChild(controls);
  container.appendChild(root);
  paint();
  return container;
}
